import { useState, useEffect } from 'react'
import axios from 'axios'
import { TraceLookup } from './TraceLookup'
import { RecentTraces, addToTraceHistory } from './RecentTraces'
import { TraceTree } from './TraceTree'
import { LatencyWaterfall } from './LatencyWaterfall'
import { TraceDetail } from './TraceDetail'
import { FolderTree, GanttChartSquare } from 'lucide-react'
import type { TraceNode } from '@/lib/utils/traceParser'

const API_BASE = import.meta.env.VITE_RAG_API_URL || 'http://localhost:8000'

function SearchIconPlaceholder() {
    return (
        <svg className="w-16 h-16 opacity-20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
    )
}

interface ObservabilityCenterProps {
    initialTraceId?: string
}

export function ObservabilityCenter({ initialTraceId }: ObservabilityCenterProps) {
    const [currentTraceId, setCurrentTraceId] = useState<string>('')
    const [traceData, setTraceData] = useState<any>(null)
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    // View Mode
    const [viewMode, setViewMode] = useState<'tree' | 'waterfall'>('tree')

    // Selection State
    const [selectedNode, setSelectedNode] = useState<TraceNode | null>(null)

    // Force re-render of RecentTraces when history updates
    const [historyTick, setHistoryTick] = useState(0)

    useEffect(() => {
        if (initialTraceId) {
            loadTrace(initialTraceId)
        }
    }, [initialTraceId])

    const loadTrace = async (id: string) => {
        setIsLoading(true)
        setError(null)
        setTraceData(null)
        setSelectedNode(null)
        setCurrentTraceId(id)

        try {
            // Using the EXACT endpoint mandated by Director
            // GET /internal/audit/tree/{trace_id}
            const { data } = await axios.get(`${API_BASE}/internal/audit/tree/${id}`)
            setTraceData(data)

            // Add to history on success
            addToTraceHistory(id, 'success')
            setHistoryTick(prev => prev + 1)

        } catch (err: any) {
            console.warn('Trace lookup failed:', err.message)
            const status = err.response?.status
            let msg = 'Failed to load trace.'

            if (status === 404) {
                msg = `Trace ID ${id} not found (404). Check if the ID is correct.`
            } else if (status === 422) {
                msg = `Invalid UUID format.`
            } else {
                msg = err.response?.data?.detail || err.message
            }

            setError(msg)
            addToTraceHistory(id, 'error')
            setHistoryTick(prev => prev + 1)
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div className="max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-6 h-[calc(100vh-140px)]">

            {/* LEFT COLUMN: Input & History (3/12) */}
            <div className="lg:col-span-3 flex flex-col gap-6 overflow-hidden">
                <TraceLookup
                    onLookup={loadTrace}
                    isLoading={isLoading}
                    error={error}
                />

                <div className="flex-1 min-h-0">
                    <RecentTraces
                        key={historyTick}
                        onSelect={loadTrace}
                        currentTraceId={currentTraceId}
                    />
                </div>
            </div>

            {/* MIDDLE COLUMN: Visualization (5/12) */}
            <div className="lg:col-span-5 bg-white/5 border border-white/10 rounded-2xl p-6 overflow-hidden flex flex-col">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold text-white">Execution View</h2>
                    <div className="flex bg-black/40 rounded-lg p-1 border border-white/10">
                        <button
                            onClick={() => setViewMode('tree')}
                            className={`p-1.5 rounded transition-all ${viewMode === 'tree' ? 'bg-blue-600 text-white shadow-lg' : 'text-gray-400 hover:text-white'}`}
                            title="Tree View"
                        >
                            <FolderTree className="w-4 h-4" />
                        </button>
                        <button
                            onClick={() => setViewMode('waterfall')}
                            className={`p-1.5 rounded transition-all ${viewMode === 'waterfall' ? 'bg-blue-600 text-white shadow-lg' : 'text-gray-400 hover:text-white'}`}
                            title="Latency Waterfall"
                        >
                            <GanttChartSquare className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                <div className="flex-1 bg-black/40 border border-white/5 rounded-xl p-4 overflow-auto custom-scrollbar relative">
                    {!traceData && !isLoading && !error && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-500">
                            <SearchIconPlaceholder />
                            <p className="mt-4 text-sm">Enter a Trace ID to inspect details.</p>
                        </div>
                    )}

                    {isLoading && (
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                        </div>
                    )}

                    {traceData && (
                        <>
                            {viewMode === 'tree' ? (
                                <TraceTree
                                    data={traceData}
                                    onNodeSelect={setSelectedNode}
                                    selectedNodeId={selectedNode?.id}
                                />
                            ) : (
                                <LatencyWaterfall
                                    data={traceData}
                                    onNodeSelect={setSelectedNode}
                                    selectedNodeId={selectedNode?.id}
                                />
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* RIGHT COLUMN: Selected Node Details (4/12) */}
            <div className="lg:col-span-4 bg-white/5 border border-white/10 rounded-2xl p-6 overflow-hidden flex flex-col">
                <h2 className="text-xl font-semibold text-white mb-4">Step Details</h2>
                <div className="flex-1 min-h-0">
                    <TraceDetail node={selectedNode} />
                </div>
            </div>
        </div>
    )
}
